import { filterBy } from "@progress/kendo-data-query";
import {
  InputPrefix,
  InputSuffix,
  TextBox,
} from "@progress/kendo-react-inputs";
import classNames from "classnames";
import isEmpty from "lodash-es/isEmpty";
import { XCircle as Close } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import SearchIcon from "@/icons/Search";

import "./style.scss";

export const Search: React.FC<{
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * Required by callers whose spec addresses this box; never defaulted, since
   * more than one Search can render on a page.
   */
  testId?: string;
  /** Accessible name, when the placeholder alone is not the label. */
  ariaLabel?: string;
  /** Extra attributes for the input itself — ARIA wiring for combobox usage. */
  inputAttributes?: React.InputHTMLAttributes<HTMLInputElement>;
}> = ({
  onChange,
  placeholder,
  value: valueProps = "",
  disabled,
  className = "",
  testId,
  ariaLabel,
  inputAttributes,
}) => {
  const [value, setValue] = useState<string>(valueProps);

  const handleChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      onChange(newValue);
    },
    [onChange],
  );

  useEffect(() => {
    if (valueProps !== undefined && value !== valueProps) {
      handleChange(valueProps);
    }
    // only need for first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueProps]);

  const suffix = useCallback(
    () => (
      <InputSuffix
        onClick={() => {
          handleChange("");
        }}
      >
        {value.length > 0 && <Close className="input-close-icon" />}
      </InputSuffix>
    ),
    [value.length, handleChange],
  );

  return (
    <form className="search" onSubmit={e => e.preventDefault()}>
      <TextBox
        inputAttributes={
          {
            ...inputAttributes,
            ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
            ...(testId ? { "data-test-id": testId } : {}),
          } as React.InputHTMLAttributes<HTMLInputElement>
        }
        prefix={() => (
          <InputPrefix>
            <SearchIcon />
          </InputPrefix>
        )}
        suffix={suffix}
        style={{
          cursor: disabled ? "not-allowed" : "",
        }}
        value={value}
        disabled={disabled}
        onChange={event => handleChange(event.value as string)}
        placeholder={placeholder ?? "Search..."}
        className={classNames("search-input", className)}
      />
    </form>
  );
};

/**
 * Client-side search over `data`. Pass `controlled` to keep the term outside
 * the hook (e.g. in the URL, so Back and refresh restore it); the debounce
 * and filtering behave the same either way.
 */
export function useClientSearch<T>(
  data: T[],
  columnsToFilter: string[],
  controlled?: { search: string; setSearch: (value: string) => void },
) {
  const [ownSearch, setOwnSearch] = useState<string>("");
  const search = controlled?.search ?? ownSearch;
  const setSearch = controlled?.setSearch ?? setOwnSearch;

  const searchValue = useDebounce(search, 300);

  const clearSearch = useCallback(() => {
    setSearch("");
  }, [setSearch]);

  const columnsKey = JSON.stringify(columnsToFilter);
  const columns = React.useMemo(
    () => columnsToFilter,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on serialized contents, not array identity
    [columnsKey],
  );

  const filterData = React.useMemo(() => {
    if (isEmpty(searchValue)) {
      return data || [];
    }
    return filterBy(data, {
      logic: "or",
      filters: columns.map(col => ({
        field: col,
        operator: "contains",
        value: searchValue?.trim(),
        ignoreCase: true,
      })),
    });
  }, [data, searchValue, columns]);

  // Tests one `buildSearchText` index against the same debounced term
  // `filterData` used. `toLowerCase` (not `toLocaleLowerCase`) matches what
  // `buildSearchText` produces - they diverge in locales such as `tr`, where
  // the index would stop matching the term searched against it.
  const term = searchValue.trim().toLowerCase();
  const matches = useCallback(
    (searchText: string) => !term || searchText.includes(term),
    [term],
  );

  // `searchValue` is the debounced term `filterData` was actually computed
  // from - callers that derive anything alongside the filtered list must use
  // it, or their derived output leads the list by one debounce interval.
  return {
    search,
    searchValue,
    term,
    setSearch,
    clearSearch,
    filterData,
    matches,
  };
}
